<template>
  <div
    v-if="state.active"
    class="dialogue-overlay"
    @click="handleClick"
  >
    <div
      class="dialogue-box"
      @click.stop
    >
      <button
        class="dialogue-close"
        aria-label="Close dialogue"
        @click="closeDialogue"
      >
        ×
      </button>

      <div class="dialogue-main">
        <!-- Portrait section -->
        <div class="dialogue-portrait-container">
          <div
            class="dialogue-portrait-frame"
            :style="{ width: portraitSize + 'px', height: portraitSize + 'px' }"
          >
            <EntityRepresentation
              v-if="portraitConfig"
              :services="services"
              :world="portraitConfig.world"
              :entity-id="portraitConfig.entityId"
              :size="portraitSize"
              :zoom="portraitZoom"
              :head-mode="true"
              @tick="handlePortraitTick"
            />
          </div>
          <div
            v-if="state.speaker"
            class="dialogue-portrait-name"
          >
            {{ state.speaker }}
          </div>
        </div>

        <!-- Content section -->
        <div class="dialogue-content">
          <div class="dialogue-text">
            {{ state.text }}
          </div>

          <div
            v-if="state.choices.length > 0"
            class="dialogue-choices"
          >
            <button
              v-for="choice in state.choices"
              :key="choice.index"
              class="dialogue-choice"
              :class="{ selected: selectedIndex === choice.index }"
              @click="selectChoice(choice.index)"
              @mouseenter="selectedIndex = choice.index"
            >
              {{ choice.index + 1 }}. {{ choice.text }}
            </button>
          </div>

          <div
            v-else-if="state.canContinue"
            class="dialogue-continue"
          >
            Click or press Space to continue...
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, shallowRef, toRaw, watch } from 'vue';

 import { ClientWorld } from '../ClientWorld';
 import { ClientDefaults } from '../components';
 import { ClientServiceContainer } from '../services/ClientServiceContainer';
 import { ClientDialogueService, DialogueState } from '../services/DialogueService';
 import { AnimationSystem } from '../systems/AnimationSystem';
 import { AnimationTransformSystem } from '../systems/AnimationTransformSystem';
 import EntityRepresentation from './EntityRepresentation.vue';
 
 const props = defineProps<{
   dialogueService: ClientDialogueService;
   services: ClientServiceContainer;
 }>();

const state = props.dialogueService.state as DialogueState;
const selectedIndex = ref(0);

// Portrait is 16x16 pixels scaled 6x = 96x96
const portraitSize = 96;
const portraitZoom = 6;

const mockWorld = shallowRef<ClientWorld | null>(null);
const mockEntityId = ref<number | null>(null);
let animSystem: AnimationSystem | null = null;
let animTransformSystem: AnimationTransformSystem | null = null;

function handleClick(): void {
  if (state.canContinue) {
    props.dialogueService.continue();
  }
}

function selectChoice(index: number): void {
  props.dialogueService.selectChoice(index);
}

function handleKeydown(event: KeyboardEvent): void {
  if (!state.active) return;

  // Escape to close dialogue
  if (event.key === 'Escape') {
    closeDialogue();
    event.preventDefault();
    return;
  }

  // Number keys 1-9 for quick choice selection
  const keyNum = parseInt(event.key);
  if (!isNaN(keyNum) && keyNum >= 1 && keyNum <= state.choices.length) {
    selectChoice(keyNum - 1);
    return;
  }

  // Arrow keys for navigation
  if (state.choices.length > 0) {
    if (event.key === 'ArrowUp' || event.key === 'w') {
      selectedIndex.value = Math.max(0, selectedIndex.value - 1);
      event.preventDefault();
    } else if (event.key === 'ArrowDown' || event.key === 's') {
      selectedIndex.value = Math.min(state.choices.length - 1, selectedIndex.value + 1);
      event.preventDefault();
    } else if (event.key === 'Enter' || event.key === ' ') {
      selectChoice(selectedIndex.value);
      event.preventDefault();
    }
  } else if (state.canContinue) {
    // Space or Enter to continue
    if (event.key === ' ' || event.key === 'Enter') {
      props.dialogueService.continue();
      event.preventDefault();
    }
  }
}

function closeDialogue(): void {
  props.dialogueService.requestExit();
}

// ============ Mock World Logic ============

function initMockWorld() {
  const world = new ClientWorld(ClientDefaults);
  mockWorld.value = world;

  // Initialize systems for mock world
  animSystem = new AnimationSystem(props.services);
  animTransformSystem = new AnimationTransformSystem(props.services);

  // Sync initial state if we have a target
  syncMockEntity();
}

function syncMockEntity() {
  if (!mockWorld.value || state.targetEntityId === null) return;

  const realWorld = props.services.clientWorld.getWorld();
  const realEntityId = props.services.clientWorld.getClientEntityId(state.targetEntityId);
  if (realEntityId === undefined) return;

  // Create or update mock entity
  if (mockEntityId.value === null) {
    mockEntityId.value = mockWorld.value.createEntity();
  }

  const mockId = mockEntityId.value;

  // Sync SpritesheetSprite
  const spriteComp = realWorld.getComponent(realEntityId, 'SpritesheetSprite');
  if (spriteComp) {
    const mockSprite = mockWorld.value.getComponent(mockId, 'SpritesheetSprite');
    if (!mockSprite) {
      mockWorld.value.addComponent(
        mockId,
        mockWorld.value.createComponent('SpritesheetSprite', { ...toRaw(spriteComp) }),
      );
    } else {
      mockWorld.value.updateComponent(mockId, mockSprite, { ...toRaw(spriteComp) });
    }

    // Force idle_down animation for portrait
    let animation = mockWorld.value.getComponent(mockId, 'Animation');
    if (!animation) {
      animation = mockWorld.value.createComponent('Animation', {
        name: 'idle_down',
        isPlaying: true,
        loop: true,
        animationFrame: 1,
        timer: 0,
      });
      mockWorld.value.addComponent(mockId, animation);
    } else if (animation.name !== 'idle_down') {
      mockWorld.value.updateComponent(mockId, animation, {
        name: 'idle_down',
        isPlaying: true,
        animationFrame: 1,
        timer: 0,
      });
    }
  }

  // Sync SkinColour
  const skin = realWorld.getComponent(realEntityId, 'SkinColour');
  if (skin) {
    const mockSkin = mockWorld.value.getComponent(mockId, 'SkinColour');
    if (!mockSkin) {
      mockWorld.value.addComponent(
        mockId,
        mockWorld.value.createComponent('SkinColour', { ...toRaw(skin) }),
      );
    } else {
      mockWorld.value.updateComponent(mockId, mockSkin, { ...toRaw(skin) });
    }
  }

  // Sync FaceDecoration
  const face = realWorld.getComponent(realEntityId, 'FaceDecoration');
  if (face) {
    const mockFace = mockWorld.value.getComponent(mockId, 'FaceDecoration');
    if (!mockFace) {
      mockWorld.value.addComponent(
        mockId,
        mockWorld.value.createComponent('FaceDecoration', { ...toRaw(face) }),
      );
    } else {
      mockWorld.value.updateComponent(mockId, mockFace, { ...toRaw(face) });
    }
  }

  // Sync Sprite (for visibility/tint)
  const spriteData = realWorld.getComponent(realEntityId, 'Sprite');
  if (spriteData) {
    const mockSpriteData = mockWorld.value.getComponent(mockId, 'Sprite');
    if (!mockSpriteData) {
      mockWorld.value.addComponent(
        mockId,
        mockWorld.value.createComponent('Sprite', { ...toRaw(spriteData) }),
      );
    } else {
      mockWorld.value.updateComponent(mockId, mockSpriteData, { ...toRaw(spriteData) });
    }
  }
}

function tickMockWorld(deltaTime: number) {
  if (!mockWorld.value || mockEntityId.value === null) return;
  animSystem?.updateEntity({
    world: toRaw(mockWorld.value),
    entityId: mockEntityId.value,
    deltaTime,
  });
  animTransformSystem?.updateEntity({
    world: toRaw(mockWorld.value),
    entityId: mockEntityId.value,
    deltaTime,
  });
}

function handlePortraitTick(deltaTime: number) {
  tickMockWorld(deltaTime);
}

const portraitConfig = computed(() => {
  if (state.active && mockWorld.value && mockEntityId.value !== null) {
    return {
      world: toRaw(mockWorld.value),
      entityId: mockEntityId.value,
    };
  }
  return null;
});

// Watch for target entity changes to sync mock entity
watch(
  () => state.targetEntityId,
  () => {
    syncMockEntity();
  },
);

// Watch for dialogue becoming active to initialize mock world
watch(
  () => state.active,
  (active) => {
    if (active) {
      if (!mockWorld.value) {
        initMockWorld();
      } else {
        syncMockEntity();
      }
    } else {
      // Dialogue closed: clear mock world and entity to avoid stale state
      if (mockWorld.value && mockEntityId.value !== null) {
        mockWorld.value.destroyEntity(mockEntityId.value);
      }
      mockWorld.value = null;
      mockEntityId.value = null;
      animSystem = null;
      animTransformSystem = null;
    }
  },
);

// Reset selected index when choices change
watch(
  () => state.choices,
  () => {
    selectedIndex.value = 0;
  },
);

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  if (mockWorld.value && mockEntityId.value !== null) {
    mockWorld.value.destroyEntity(mockEntityId.value);
  }
  mockWorld.value = null;
  mockEntityId.value = null;
  animSystem = null;
  animTransformSystem = null;
});
</script>

<style scoped>
.dialogue-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 20px;
  pointer-events: auto;
  z-index: 1000;
}

.dialogue-box {
  position: relative;
  background: linear-gradient(180deg, rgba(20, 25, 35, 0.95) 0%, rgba(10, 12, 18, 0.98) 100%);
  border: 2px solid rgba(100, 140, 180, 0.4);
  border-radius: 8px;
  padding: 20px 24px;
  max-width: 700px;
  width: 100%;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.6),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

.dialogue-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  background: rgba(60, 70, 90, 0.5);
  border: 1px solid rgba(100, 140, 180, 0.3);
  border-radius: 4px;
  color: rgba(180, 190, 200, 0.8);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialogue-close:hover {
  background: rgba(100, 70, 70, 0.6);
  border-color: rgba(180, 100, 100, 0.5);
  color: #fff;
}

/* Main layout: portrait on left, content on right */
.dialogue-main {
  display: flex;
  gap: 16px;
}

/* Portrait container with canvas and speaker name */
.dialogue-portrait-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.dialogue-portrait-frame {
  background: rgba(40, 50, 70, 0.5);
  border: 2px solid rgba(100, 140, 180, 0.3);
  border-radius: 4px;
  overflow: hidden;
}

.dialogue-portrait-name {
  color: #7cb3e8;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 6px;
  text-align: center;
  max-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Content area: text and choices */
.dialogue-content {
  flex: 1;
  min-width: 0;
}

.dialogue-text {
  color: #e8ecf2;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 16px;
}

.dialogue-choices {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dialogue-choice {
  background: rgba(60, 80, 110, 0.3);
  border: 1px solid rgba(100, 140, 180, 0.3);
  border-radius: 4px;
  padding: 10px 14px;
  color: #c8d4e2;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
}

.dialogue-choice:hover,
.dialogue-choice.selected {
  background: rgba(80, 120, 160, 0.4);
  border-color: rgba(120, 170, 220, 0.6);
  color: #ffffff;
}

.dialogue-continue {
  color: rgba(180, 190, 200, 0.6);
  font-size: 13px;
  font-style: italic;
  text-align: center;
}
</style>
